import { FC, useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { useBranding, useServices, useService } from "@utils/api";
import Screening from "@components/Screening";
import useBase64Encryption from "@hooks/useBase64Encryption";
import { WizardPayload } from "@components/SharedTypes";
import { BookingProvider, useBookingContext } from "@contexts/BookingContext";
import { Skeleton } from "@/components/ui/skeleton";
import LanguageSelector from "@/components/common/LanguageSelector";
import { emitWidgetEvent } from "@utils/widgetEvents";
import { addFlowBreadcrumb } from "@utils/breadcrumbs";

// Import the new step components
import WizardHeader from "./WizardHeader";
import ServiceSelectionStep from "./ServiceSelectionStep";
import AppointmentSelectionStep from "./AppointmentSelectionStep";
import PatientInformationStep from "./PatientInformationStep";
import PatientConfirmationStep from "./PatientConfirmationStep";
import CompletionStep from "./CompletionStep";
import WizardNavigation from "./WizardNavigation";
import { Stepper } from "@/components/ui";

type WizardProps = {
  serviceId?: string
  stickyHeader?: boolean
}

// Inner component that uses the BookingContext
const BookingWizardContent: FC<WizardProps> = ({ serviceId, stickyHeader = true }) => {
  const { t, i18n } = useTranslation();
  const { branding, isLoading } = useBranding();
  const { services } = useServices({ language: i18n.language });
  const { service: preSelectedService, isLoading: isServiceLoading } = useService(serviceId, i18n.language);

  const {
    currentStep,
    isCurrentStep,
    goForward,
    goBack,
    restart,
    bookingData,
    updateBookingData,
    getStepTitle,
    clickWrapShown,
    setClickWrapShown,
    screeningStartedAt
  } = useBookingContext();

  const encrypt = useBase64Encryption();

  // Ref for step content area - used for focus management
  const stepContentRef = useRef<HTMLDivElement>(null);
  const previousStepRef = useRef(currentStep);

  // Scroll and focus management when step changes
  useEffect(() => {
    if (previousStepRef.current !== currentStep) {
      previousStepRef.current = currentStep;
      requestAnimationFrame(() => {
        // Find the scrollable parent (sheet/dialog content area) or use window
        const scrollableParent = stepContentRef.current?.closest('[data-slot="scrollable-content"], [data-slot="dialog-content"]');
        if (scrollableParent) {
          // Scroll the container to top
          scrollableParent.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          // Fallback to window scroll
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        }
        stepContentRef.current?.focus({ preventScroll: true });
      });
    }
  }, [currentStep]);

  // Check if a service was pre-selected via query param
  const hasPreSelectedService = !!preSelectedService;

  // Check if this is a single-service auto-skip scenario
  const isSingleServiceMode = services?.length === 1 && !preSelectedService;

  // Derive selectedService from hooks (always uses freshly-fetched translated data)
  const selectedService = useMemo(() => {
    if (!bookingData.selectedServiceId) return undefined;
    // Prefer preSelectedService if it matches (already fetched with correct language)
    if (preSelectedService?.id === bookingData.selectedServiceId) return preSelectedService;
    // Otherwise find in services list
    return services?.find(s => s.id === bookingData.selectedServiceId);
  }, [bookingData.selectedServiceId, preSelectedService, services]);

  // Track if we're on the first screening question (for language selector positioning)
  const [isFirstScreeningQuestion, setIsFirstScreeningQuestion] = useState(true);

  // Screening completion handler
  const onScreeningComplete = (payload: WizardPayload) => {
    emitWidgetEvent('screeningCompleted');
    addFlowBreadcrumb('Screening completed', {
      nextStep: selectedService?.appointmentTimeRequired ? 'appointmentSelection' : 'patientInformation',
      responseCount: payload.responses.length,
      attachmentCount: Object.keys(payload.attachments).length,
    });
    if (selectedService?.appointmentTimeRequired) {
      goForward('appointmentSelection');
    } else {
      goForward('patientInformation');
    }

    const { responses, patientChartAttributes, attachments, triggeredActionKeys } = payload;

    encrypt(JSON.stringify(responses)).then(({encryptedValue, encryptionKey}) => {
      const responsePayload = {
        encryptedResponse: encryptedValue,
        encryptionKey,
        status: 'completed',
        formId: selectedService?.screenFormId,
        attachmentIds: Object.keys(attachments),
        ...(screeningStartedAt ? { startedAt: screeningStartedAt } : {}),
        ...(triggeredActionKeys?.length ? { triggeredActionKeys } : {})
      };
      updateBookingData({
        responsesAttributes: [responsePayload]
      });
    });
    
    const chartAttributePromises = patientChartAttributes.map(async (attr: any) => {
      const {encryptedValue, encryptionKey} = await encrypt(attr.value);
      return {
        ...attr,
        value: encryptedValue,
        encryptionKey
      };
    });
    
    Promise.all(chartAttributePromises).then((chartAttributesArray) => 
      updateBookingData({
        patientChartAttributesAttributes: chartAttributesArray
      })
    );
  };

  // For single-service mode, skip serviceSelection in stepper and start with screeningForm
  const isSingleServiceFlowLocal = hasPreSelectedService || isSingleServiceMode;

  const singleServiceSteps: Record<string, { title: string; description: string }> = {
    screeningForm: {
      description: '',
      title: t('steps.screeningFormSingleService')
    },
    appointmentSelection: { description: '', title: getStepTitle('appointmentSelection') },
    patientInformation: { description: '', title: getStepTitle('patientInformation') },
    patientConfirmation: { description: '', title: getStepTitle('patientConfirmation') }
  };

  const multiServiceSteps: Record<string, { title: string; description: string }> = {
    serviceSelection: { description: '', title: getStepTitle('serviceSelection') },
    screeningForm: { description: '', title: getStepTitle('screeningForm') },
    appointmentSelection: { description: '', title: getStepTitle('appointmentSelection') },
    patientInformation: { description: '', title: getStepTitle('patientInformation') },
    patientConfirmation: { description: '', title: getStepTitle('patientConfirmation') }
  };

  const steps = isSingleServiceFlowLocal ? singleServiceSteps : multiServiceSteps;

  const handleBack = () => {
    // In single-service mode, restart from appointmentSelection
    if (isSingleServiceMode && isCurrentStep('appointmentSelection')) {
      restart();
      return;
    }
    if (isCurrentStep('appointmentSelection')) {
      restart();
      return;
    }
    goBack();
  };

  if (isLoading || isServiceLoading) return <Skeleton className="w-full h-64" />;
  if (!branding) return null;

  return (
    <main
      className="w-full text-center"
      aria-label={t('aria.bookingWizard', { clinicName: branding.name })}
    >
      <div className="w-full">
        {/* Sticky header with logo, language selector, title, and progress bar */}
        <div className={stickyHeader ? "sticky top-0 z-20 bg-background pt-2 space-y-3" : "bg-background space-y-3"}>
          <WizardHeader
            currentStep={currentStep}
            supportedLanguages={branding?.supportedLanguages}
            hasPreSelectedService={hasPreSelectedService}
          />

          {/* Language selector on first screening question for single-service flow */}
          {isSingleServiceFlowLocal && isCurrentStep('screeningForm') && isFirstScreeningQuestion && branding?.supportedLanguages && (
            <LanguageSelector supportedLanguages={branding.supportedLanguages} />
          )}

          {!isCurrentStep('complete') && (
            <>
              <h1 className="text-sm sm:text-lg font-medium">
                {isSingleServiceFlowLocal && selectedService?.name && !isCurrentStep('serviceSelection')
                  ? t('booking.singleServiceTitle', { serviceName: selectedService.name })
                  : t('booking.title')}
              </h1>
              <Stepper steps={steps} activeStep={currentStep} />
            </>
          )}
        </div>

        {!isCurrentStep('complete') && (
          <div className="relative z-10 w-full mx-auto space-y-4">
            <div>
              {/* Step Content Container - manages focus on step changes */}
              <div
                ref={stepContentRef}
                tabIndex={-1}
                className="outline-none"
                aria-label={getStepTitle(currentStep)}
              >
                {/* Service Selection Step */}
                {isCurrentStep('serviceSelection') && (
                  <ServiceSelectionStep preSelectedService={preSelectedService} />
                )}

                {/* Screening Form Step */}
                {isCurrentStep('screeningForm') && selectedService?.screenFormId && (
                  <Screening
                    formId={selectedService.screenFormId}
                    onComplete={onScreeningComplete}
                    onBack={isSingleServiceFlowLocal ? undefined : handleBack}
                    onFirstQuestionChange={isSingleServiceFlowLocal ? setIsFirstScreeningQuestion : undefined}
                    showClickWrap={isSingleServiceFlowLocal && !clickWrapShown}
                    onClickWrapShown={() => setClickWrapShown(true)}
                  />
                )}

                {/* Appointment Selection Step */}
                {isCurrentStep('appointmentSelection') && (
                  <AppointmentSelectionStep isSingleServiceFlow={isSingleServiceFlowLocal} />
                )}

                {/* Patient Information Step */}
                {isCurrentStep('patientInformation') && (
                  <PatientInformationStep selectedService={selectedService} />
                )}

                {/* Patient Confirmation Step */}
                {isCurrentStep('patientConfirmation') && (
                  <PatientConfirmationStep />
                )}
              </div>
              <div className="mb-2"></div>
              {/* Navigation */}
              <WizardNavigation
                language={i18n.language}
              />
            </div>
          </div>
        )}

        {/* Completion Step */}
        {isCurrentStep('complete') && (
          <CompletionStep selectedService={selectedService} />
        )}
      </div>
    </main>
  );
};

// Main wrapper component with BookingProvider
const BookingWizard: FC<WizardProps> = ({ serviceId, stickyHeader = true }) => {
  return (
    <BookingProvider>
      <BookingWizardContent serviceId={serviceId} stickyHeader={stickyHeader} />
    </BookingProvider>
  );
};

export default BookingWizard;
